import { useProjectActions } from "@contexts/projects";
import { useEffect, useState } from "react";
import CycleSelect from "@components/dashboard/CycleSelect";

const genStageKey = () =>
  `stage_${globalThis.crypto?.randomUUID?.() || (Date.now() + "_" + Math.floor(Math.random() * 1e6))}`;

const GRID = { display: "grid", gridTemplateColumns: "1fr 190px 90px auto auto", gap: "8px", alignItems: "center" };

// Cadastro dos ciclos de compra da construtora (Cronograma de Suprimentos):
// N ciclos nomeados por empresa, um deles o padrão (não renomeia/arquiva).
const ProcurementCyclesSection = ({ company }) => {
  const { getProcurementTemplate, updateProcurementTemplate } = useProjectActions();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stagesDraft, setStagesDraft] = useState([]);
  const [nameDraft, setNameDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCycle, setNewCycle] = useState({ name: "", baseId: "" });
  const [msg, setMsg] = useState("");

  const selected = templates.find((t) => t._id === selectedId);

  const load = async (keepId) => {
    const res = await getProcurementTemplate(company._id, true);
    const list = res?.templates || [];
    setTemplates(list);
    const keep = keepId && list.find((t) => t._id === keepId);
    setSelectedId((keep || list.find((t) => t.is_default) || list[0])?._id ?? null);
  };

  useEffect(() => { if (company?._id) load(); }, [company?._id]);

  useEffect(() => {
    if (!selected) return;
    setStagesDraft(selected.stages);
    setNameDraft(selected.name);
    setCreating(false);
    setMsg("");
  }, [selectedId, templates]);

  const flash = (m, ok = false) => {
    setMsg(m);
    if (ok) setTimeout(() => setMsg(""), 3000);
  };

  const updateStageField = (idx, field, value) =>
    setStagesDraft((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  const moveStage = (idx, dir) =>
    setStagesDraft((prev) => {
      const j = dir === "up" ? idx - 1 : idx + 1;
      if (j < 0 || j >= prev.length) return prev;
      const arr = [...prev];
      const tmp = arr[idx];
      arr[idx] = arr[j];
      arr[j] = tmp;
      return arr;
    });
  const removeStage = (idx) => setStagesDraft((prev) => prev.filter((_, i) => i !== idx));
  const addStage = () =>
    setStagesDraft((prev) => [...prev, { key: genStageKey(), name: "", area: "", order: prev.length + 1, lead_time_days: 0 }]);

  const normalizedStages = () =>
    stagesDraft.map((s, i) => ({
      key: s.key, name: (s.name || "").trim(), area: s.area || "", order: i + 1,
      lead_time_days: Math.max(0, parseInt(s.lead_time_days, 10) || 0),
    }));

  const saveStages = async () => {
    const res = await updateProcurementTemplate(company._id, { action: "update", template_id: selectedId, stages: normalizedStages() });
    if (res?.template) { flash("Salvo!", true); load(selectedId); } else flash(res?.message || "Erro ao salvar");
  };

  const renameCycle = async () => {
    const res = await updateProcurementTemplate(company._id, { action: "rename", template_id: selectedId, name: nameDraft.trim() });
    if (res?.template) { flash("Salvo!", true); load(selectedId); } else flash(res?.message || "Erro ao renomear");
  };

  const archiveCycle = async () => {
    const res = await updateProcurementTemplate(company._id, { action: "archive", template_id: selectedId });
    if (res?.template) { flash("Ciclo arquivado", true); load(); } else flash(res?.message || "Erro ao arquivar");
  };

  const createCycle = async () => {
    const base = templates.find((t) => t._id === (newCycle.baseId || selectedId)) || templates.find((t) => t.is_default);
    const res = await updateProcurementTemplate(company._id, { action: "create", name: newCycle.name.trim(), stages: base?.stages || [] });
    if (res?.template) {
      setCreating(false);
      setNewCycle({ name: "", baseId: "" });
      flash("Ciclo criado", true);
      await load(res.template._id);
    } else flash(res?.message || "Erro ao criar ciclo");
  };

  return (
    <>
      <p className="helper">
        Configuração da construtora: os ciclos de compra e as etapas de cada um, com os dias (lead time) de cada etapa.
        A última etapa é a necessidade do insumo na obra (os dias dela são ignorados). Mudar as etapas re-deriva o
        cronograma dos insumos daquele ciclo. Cada insumo pode usar o ciclo padrão ou outro ciclo (escolhido no card).
      </p>

      <div className="procurement-cycle-picker margin-left-16 margin-top-8">
        <CycleSelect templates={templates} value={selectedId} onChange={setSelectedId} />
        <button className="button button-secondary small" onClick={() => { setCreating(true); setMsg(""); }}>+ Novo ciclo</button>
      </div>

      {creating && (
        <div className="flex gap-8 margin-left-16 margin-top-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <input
            className="default-input"
            style={{ width: "220px" }}
            placeholder="Nome do novo ciclo (ex.: Elevador)"
            value={newCycle.name}
            onChange={(e) => setNewCycle((p) => ({ ...p, name: e.target.value }))}
          />
          <select value={newCycle.baseId || selectedId || ""} onChange={(e) => setNewCycle((p) => ({ ...p, baseId: e.target.value }))}>
            {templates.map((t) => (
              <option key={t._id} value={t._id}>Copiar etapas de: {t.name}</option>
            ))}
          </select>
          <button className="button button-accent small" disabled={!newCycle.name.trim()} onClick={createCycle}>Criar</button>
          <button className="button button-secondary small" onClick={() => setCreating(false)}>Cancelar</button>
        </div>
      )}

      {selected && (
        <>
          {!selected.is_default && (
            <div className="flex gap-8 margin-left-16 margin-top-8" style={{ alignItems: "center" }}>
              <input
                className="default-input"
                style={{ width: "220px" }}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
              />
              <button className="button button-secondary small" disabled={!nameDraft.trim() || nameDraft.trim() === selected.name} onClick={renameCycle}>Renomear</button>
              <button className="button button-secondary small" title="O ciclo some do cadastro; insumos que já o usam continuam funcionando" onClick={archiveCycle}>Arquivar ciclo</button>
            </div>
          )}

          <div className="margin-left-16 margin-top-8" style={{ ...GRID, fontSize: ".75rem", fontWeight: 600, opacity: 0.6 }}>
            <span>Etapa</span><span>Área</span><span>Dias</span><span /><span />
          </div>
          {stagesDraft.map((s, idx) => (
            <div key={s.key} className="margin-left-16 margin-top-8" style={GRID}>
              <input className="default-input" style={{ width: "100%" }} placeholder="Nome da etapa" value={s.name} onChange={(e) => updateStageField(idx, "name", e.target.value)} />
              <input className="default-input" style={{ width: "100%" }} placeholder="Área" value={s.area || ""} onChange={(e) => updateStageField(idx, "area", e.target.value)} />
              <input className="default-input" style={{ width: "100%" }} type="number" min={0} placeholder="Dias" value={s.lead_time_days} onChange={(e) => updateStageField(idx, "lead_time_days", e.target.value)} />
              <div className="flex">
                <button className="arrow-icon arrow-icon-up" title="Subir" onClick={() => moveStage(idx, "up")} />
                <button className="arrow-icon arrow-icon-down" title="Descer" onClick={() => moveStage(idx, "down")} />
              </div>
              <button title="Remover etapa" onClick={() => removeStage(idx)}>
                <i className="icon icon-width-24 icon-times background-accent" style={{ backgroundColor: "tomato" }} aria-hidden="true" />
              </button>
            </div>
          ))}
          <div className="flex gap-8 margin-left-16 margin-top-8">
            <button className="button button-secondary small" onClick={addStage}>Adicionar etapa</button>
            <button className="button button-accent" onClick={saveStages}>Salvar etapas</button>
          </div>
        </>
      )}
      {msg && <p style={{ color: /Salvo|criado|arquivado/.test(msg) ? "green" : "tomato", fontStyle: "italic" }}>{msg}</p>}
    </>
  );
};

export default ProcurementCyclesSection;
